import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Radio, QrCode, Megaphone, Building2 } from "lucide-react";
import { MwHeader } from "@/components/mediaweb/MwHeader";
import { MwFooter } from "@/components/mediaweb/MwFooter";

const packs = [
  {
    name: "Essentiel",
    price: "6 000 €",
    suffix: "+ 50 €/mois hébergement",
    desc: "Pour les marques ambitieuses, showrooms et restaurants haut de gamme.",
    features: [
      "PWA radio à votre marque (logo, couleurs, identité)",
      "Playlist initiale sur mesure (jusqu'à 500 titres)",
      "Hébergement & diffusion 1 an inclus",
      "Présence sur le mur WallTunes",
      "Compatible CarPlay & Android Auto",
      "Métadonnées Now Playing + pochettes HD",
    ],
    highlight: false,
  },
  {
    name: "Premium",
    price: "8 500 €",
    suffix: "+ 80 €/mois hébergement",
    desc: "Pour les chaînes de magasins et enseignes nationales.",
    features: [
      "Tout l'Essentiel inclus",
      "Flyers print professionnels (création + maquettes prêtes à l'impression)",
      "QR codes designés pour vos points de vente",
      "4 jingles personnalisés (création studio)",
      "6 mises à jour playlist par an",
      "Rapport statistiques d'écoute mensuel",
    ],
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "dès 12 000 €",
    suffix: "sur devis personnalisé",
    desc: "Pour les centres commerciaux, groupes et marques institutionnelles.",
    features: [
      "Tout le Premium inclus",
      "Multi-sites & multi-radios",
      "Jingles publicitaires illimités",
      "Support prioritaire avec SLA",
      "Intégration corporate (SSO, branding avancé)",
      "Statistiques avancées + exports CSV/API",
      "Accompagnement stratégie média",
    ],
    highlight: false,
  },
];

const strategy = [
  {
    icon: QrCode,
    title: "QR codes en magasin",
    text: "Vos clients flashent en caisse, sur un présentoir, sur un sac. Ils continuent d'écouter votre radio sur leur trajet retour, à la maison, au bureau.",
  },
  {
    icon: Megaphone,
    title: "Flyers print pros",
    text: "Inclus dans le pack Premium et Enterprise. Maquettes prêtes à imprimer, design cohérent avec votre identité visuelle.",
  },
  {
    icon: Building2,
    title: "Présence WallTunes",
    text: "Votre radio rejoint notre mur WallTunes, accessible en CarPlay et Android Auto — un canal de découverte supplémentaire pour vos clients.",
  },
];

export default function OfferPwaPage() {
  return (
    <div className="mw-app">
      <MwHeader />

      <section className="mw-section mw-section-hero-page">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Offre Premium PWA</span>
          <h1 className="mw-h1 mw-h1-small">
            Créer la radio de votre marque
          </h1>
          <p className="mw-section-lead">
            Une application installable, brandée, professionnelle. Conçue pour
            les centres commerciaux, chaînes de magasins, showrooms et marques
            qui veulent transformer leur ambiance sonore en média propriétaire.
          </p>
        </div>
      </section>

      {/* ===== Stratégie ===== */}
      <section className="mw-section">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">La méthode MediaWebRadio</span>
          <h2 className="mw-h2">Faire vivre votre radio au-delà du magasin</h2>
        </div>

        <div className="mw-cards-3">
          {strategy.map(({ icon: Icon, title, text }) => (
            <article key={title} className="mw-card">
              <span className="mw-card-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={1.8} />
              </span>
              <h3 className="mw-card-title">{title}</h3>
              <p className="mw-card-text">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ===== Tarifs ===== */}
      <section className="mw-section">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Nos forfaits</span>
          <h2 className="mw-h2">Trois paliers, une seule promesse de qualité</h2>
          <p className="mw-section-lead">
            Tous nos forfaits débutent par un entretien stratégique gratuit pour
            cadrer votre projet. Devis personnalisé sous 48 h après notre échange.
          </p>
        </div>

        <div className="mw-packs">
          {packs.map((p) => (
            <article
              key={p.name}
              className={p.highlight ? "mw-pack mw-pack-highlight" : "mw-pack"}
              data-testid={`offer-pack-${p.name.toLowerCase()}`}
            >
              {p.highlight && <div className="mw-pack-ribbon">Le plus choisi</div>}
              <div className="mw-pack-name">{p.name}</div>
              <div className="mw-pack-price">{p.price}</div>
              <div className="mw-pack-suffix">{p.suffix}</div>
              <p className="mw-pack-desc">{p.desc}</p>
              <ul className="mw-pack-list">
                {p.features.map((f) => (
                  <li key={f}>
                    <Check size={14} strokeWidth={2.4} className="mw-pack-check" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={
                  p.highlight
                    ? "mw-btn mw-btn-primary mw-pack-cta"
                    : "mw-btn mw-btn-ghost mw-pack-cta"
                }
              >
                Demander un devis <ArrowUpRight size={14} />
              </Link>
            </article>
          ))}
        </div>

        <div className="mw-pack-note">
          Tous les tarifs sont HT, hors frais de déplacement éventuels. Facturation
          par MediaWebRadio Ltd (Dover, UK). Aucun paiement en ligne pour la
          création de PWA — votre projet débute par un entretien et un devis personnalisé.
        </div>
      </section>

      {/* ===== Alternative Stream ===== */}
      <section className="mw-section">
        <div className="mw-stream-card mw-stream-card-soft">
          <div className="mw-stream-card-inner">
            <div className="mw-stream-card-left">
              <span className="mw-pack-ribbon mw-stream-ribbon">
                <Radio size={12} /> Alternative · 50 €/an
              </span>
              <h3 className="mw-stream-title">
                Vous avez déjà votre radio ? Optez pour le Stream-only.
              </h3>
              <p className="mw-stream-desc">
                Si vous gérez déjà une webradio et souhaitez juste être présent
                sur WallTunes, notre offre Stream-only à 50 €/an est faite pour
                vous. Aucune PWA à développer, intégration en 48 h.
              </p>
            </div>
            <div className="mw-stream-card-right">
              <Link to="/offre-stream" className="mw-btn mw-btn-ghost">
                Découvrir l'offre Stream <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA Final ===== */}
      <section className="mw-section mw-section-final">
        <div className="mw-final">
          <h2 className="mw-h2">Lançons votre radio ensemble</h2>
          <p className="mw-lead">
            Un entretien stratégique gratuit pour comprendre votre marque, votre
            clientèle et vos objectifs. Mise en place en 4 semaines.
          </p>
          <div className="mw-hero-cta">
            <Link to="/contact" className="mw-btn mw-btn-primary">
              Démarrer mon projet <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <MwFooter />
    </div>
  );
}
